export const AFFAIR_STAGES = [
  { key: 'to_qualify', label: 'À qualifier', probability: 10, next: ['qualified', 'lost', 'abandoned'] },
  { key: 'qualified', label: 'Qualifiée', probability: 25, next: ['estimating', 'lost', 'abandoned'] },
  { key: 'estimating', label: 'Chiffrage', probability: 50, next: ['offer_sent', 'lost', 'abandoned'] },
  { key: 'offer_sent', label: 'Offre envoyée', probability: 65, next: ['negotiation', 'won', 'lost', 'abandoned'] },
  { key: 'negotiation', label: 'Négociation', probability: 85, next: ['won', 'lost', 'abandoned'] },
  { key: 'won', label: 'Gagnée', probability: 100, final: true, next: [] },
  { key: 'lost', label: 'Perdue', probability: 0, final: true, next: [] },
  { key: 'abandoned', label: 'Abandonnée', probability: 0, final: true, next: [] },
];

export const AFFAIR_STAGE_GROUPS = [
  { key: 'to_review', label: 'À qualifier', stages: ['to_qualify', 'qualified'] },
  { key: 'quoting', label: 'Devis en cours', stages: ['estimating'] },
  { key: 'waiting_client', label: 'En attente du client', stages: ['offer_sent', 'negotiation'] },
  { key: 'accepted', label: 'Acceptée', stages: ['won'] },
  { key: 'declined', label: 'Refusée', stages: ['lost'] },
  { key: 'abandoned', label: 'Abandonnée', stages: ['abandoned'] },
];

export const AFFAIR_PRIORITIES = ['low', 'normal', 'high', 'critical'];
export const AFFAIR_PROJECT_TYPES = ['Tertiaire', 'Logement', 'Enseignement', 'Santé', 'Industrie', 'Data center', 'Traction', 'Nucléaire'];
export const AFFAIR_FINAL_STAGES = AFFAIR_STAGES.filter((stage) => stage.final).map((stage) => stage.key);

export const AFFAIR_TYPES = [
  { key: 'project', label: 'Projet' },
  { key: 'intervention', label: 'Intervention' },
]; // Compatibilité historique : le mode est désormais choisi au démarrage de la réalisation.

const STAGE_BY_KEY = new Map(AFFAIR_STAGES.map((stage) => [stage.key, stage]));
const TYPE_BY_KEY = new Map(AFFAIR_TYPES.map((type) => [type.key, type]));
const read = (source, camelKey, snakeKey = camelKey) => source?.[camelKey] ?? source?.[snakeKey];
const emptyToNull = (value) => value === '' || value === undefined ? null : value;
const numericOrNull = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const getAffairStage = (stageKey) => STAGE_BY_KEY.get(stageKey) || STAGE_BY_KEY.get('to_qualify');
export const getAffairType = (typeKey) => TYPE_BY_KEY.get(typeKey) || TYPE_BY_KEY.get('project');
export const getAffairProbability = (stageKey) => getAffairStage(stageKey).probability;
export const getAllowedAffairTransitions = (stageKey) => [...getAffairStage(stageKey).next];
export const getPreviousAffairStage = (stageKey) => {
  const currentIndex = AFFAIR_STAGES.findIndex((stage) => stage.key === stageKey);
  if (currentIndex <= 0 || currentIndex >= AFFAIR_STAGES.findIndex((stage) => stage.final)) return null;
  return AFFAIR_STAGES[currentIndex - 1];
};
export const isAffairFinal = (stageKey) => AFFAIR_FINAL_STAGES.includes(stageKey);
export const canTransitionAffair = (fromStage, toStage) => {
  const previousStage = getPreviousAffairStage(fromStage);
  return getAllowedAffairTransitions(fromStage).includes(toStage) || previousStage?.key === toStage;
};

export const createEmptyAffairForm = (overrides = {}) => ({
  companyId: '',
  agencyId: '',
  primaryContactId: '',
  ownerUserId: '',
  technicalLeadUserId: '',
  estimatorUserId: '',
  name: '',
  label: '',
  description: '',
  source: '',
  opportunitySource: '',
  stage: 'to_qualify',
  probabilityPercent: getAffairProbability('to_qualify'),
  manualProbabilityPercent: null,
  priority: 'normal',
  estimatedAmount: '',
  expectedOrderDate: '',
  desiredStartDate: '',
  desiredEndDate: '',
  responseDeadline: '',
  dateConsultationReceived: '',
  consultationReference: '',
  projectType: '',
  buildingType: '',
  surface: '',
  workType: '',
  electricalDeparturesCount: '',
  earthingSystem: '',
  cfaPointsCount: '',
  cfaSystemsCount: '',
  ssiDevicesCount: '',
  ssiZonesCount: '',
  pvModulesCount: '',
  pvStringsCount: '',
  pvPowerKwp: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  regionOrState: '',
  postalCode: '',
  countryCode: 'FR',
  requirements: null,
  management: null,
  nextcloudPath: '',
  ...overrides,
});

export const affairFromApi = (data = {}) => {
  const stage = read(data, 'stage') || 'to_qualify';
  const canonicalRealizations = Array.isArray(data.Projects || data.projects) ? (data.Projects || data.projects) : [];
  const legacyInterventions = Array.isArray(data.Interventions || data.interventions) ? (data.Interventions || data.interventions) : [];
  const canonicalLegacyIds = new Set(canonicalRealizations.map((item) => Number(item.legacyInterventionId ?? item.legacy_intervention_id)).filter(Number.isFinite));
  const legacyOnly = legacyInterventions.filter((item) => !canonicalLegacyIds.has(Number(item.id)));
  const realizations = [...canonicalRealizations, ...legacyOnly.map((item) => ({ ...item, realizationType: 'intervention', realization_type: 'intervention' }))];
  const projects = realizations.filter((item) => (item.realizationType ?? item.realization_type ?? 'project') !== 'intervention');
  const interventions = realizations.filter((item) => (item.realizationType ?? item.realization_type) === 'intervention');
  return {
    ...data,
    id: read(data, 'id'),
    companyId: read(data, 'companyId', 'company_id') ?? '',
    agencyId: read(data, 'agencyId', 'agency_id') ?? '',
    primaryContactId: read(data, 'primaryContactId', 'primary_contact_id') ?? '',
    ownerUserId: read(data, 'ownerUserId', 'owner_user_id') ?? '',
    technicalLeadUserId: read(data, 'technicalLeadUserId', 'technical_lead_user_id') ?? '',
    estimatorUserId: read(data, 'estimatorUserId', 'estimator_user_id') ?? '',
    code: read(data, 'code') || '',
    name: read(data, 'name') || '',
    label: read(data, 'label') || '',
    description: read(data, 'description') || '',
    source: read(data, 'source') || '',
    opportunitySource: read(data, 'opportunitySource', 'opportunity_source') || '',
    stage,
    probabilityPercent: Number(read(data, 'probabilityPercent', 'probability_percent') ?? getAffairProbability(stage)),
    manualProbabilityPercent: read(data, 'manualProbabilityPercent', 'manual_probability_percent') ?? null,
    priority: read(data, 'priority') || 'normal',
    lossReason: read(data, 'lossReason', 'loss_reason') || '',
    estimatedAmount: numericOrNull(read(data, 'estimatedAmount', 'estimated_amount')),
    expectedOrderDate: read(data, 'expectedOrderDate', 'expected_order_date') || '',
    desiredStartDate: read(data, 'desiredStartDate', 'desired_start_date') || '',
    desiredEndDate: read(data, 'desiredEndDate', 'desired_end_date') || '',
    responseDeadline: read(data, 'responseDeadline', 'response_deadline') || '',
    dateConsultationReceived: read(data, 'dateConsultationReceived', 'date_consultation_received') || '',
    consultationReference: read(data, 'consultationReference', 'consultation_reference') || '',
    projectType: read(data, 'projectType', 'project_type') || '',
    buildingType: read(data, 'buildingType', 'building_type') || '',
    surface: numericOrNull(read(data, 'surface')),
    workType: read(data, 'workType', 'work_type') || '',
    addressLine1: read(data, 'addressLine1', 'address_line1') || '',
    addressLine2: read(data, 'addressLine2', 'address_line2') || '',
    city: read(data, 'city') || '',
    regionOrState: read(data, 'regionOrState', 'region_or_state') || '',
    postalCode: read(data, 'postalCode', 'postal_code') || '',
    countryCode: read(data, 'countryCode', 'country_code') || 'FR',
    requirements: read(data, 'requirements', 'requirements_json') ?? null,
    management: read(data, 'management', 'management_json') ?? null,
    nextcloudPath: read(data, 'nextcloudPath', 'nextcloud_path') || '',
    wonAt: read(data, 'wonAt', 'won_at') || null,
    lostAt: read(data, 'lostAt', 'lost_at') || null,
    createdAt: read(data, 'createdAt', 'created_at') || null,
    updatedAt: read(data, 'updatedAt', 'updated_at') || null,
    lastActivityAt: read(data, 'lastActivityAt', 'last_activity_at') || null,
    nextFollowUpAt: read(data, 'nextFollowUpAt', 'next_follow_up_at') || null,
    client: data.Client || data.client || null,
    realizations,
    projects,
    interventions,
  };
};

export const affairToApiPayload = (form = {}) => {
  const stage = form.stage || 'to_qualify';
  return {
    companyId: emptyToNull(form.companyId),
    agencyId: emptyToNull(form.agencyId),
    primaryContactId: emptyToNull(form.primaryContactId),
    ownerUserId: emptyToNull(form.ownerUserId),
    technicalLeadUserId: emptyToNull(form.technicalLeadUserId),
    estimatorUserId: emptyToNull(form.estimatorUserId),
    name: form.name?.trim() || '',
    label: form.label?.trim() || null,
    description: form.description?.trim() || null,
    source: form.source?.trim() || null,
    opportunitySource: form.opportunitySource?.trim() || null,
    stage,
    probabilityPercent: getAffairProbability(stage),
    priority: form.priority || 'normal',
    estimatedAmount: numericOrNull(form.estimatedAmount),
    expectedOrderDate: emptyToNull(form.expectedOrderDate),
    desiredStartDate: emptyToNull(form.desiredStartDate),
    desiredEndDate: emptyToNull(form.desiredEndDate),
    responseDeadline: emptyToNull(form.responseDeadline),
    dateConsultationReceived: emptyToNull(form.dateConsultationReceived),
    consultationReference: emptyToNull(form.consultationReference),
    projectType: emptyToNull(form.projectType),
    buildingType: emptyToNull(form.buildingType),
    surface: numericOrNull(form.surface),
    workType: emptyToNull(form.workType),
    addressLine1: emptyToNull(form.addressLine1),
    addressLine2: emptyToNull(form.addressLine2),
    city: emptyToNull(form.city),
    regionOrState: emptyToNull(form.regionOrState),
    postalCode: emptyToNull(form.postalCode),
    countryCode: form.countryCode || 'FR',
    requirements: form.requirements ?? null,
    management: form.management ?? null,
    nextcloudPath: emptyToNull(form.nextcloudPath),
  };
};
